import { Component, OnDestroy, OnInit } from '@angular/core';
import { CrudSaveComponent } from '../_directives';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import {
  AlertService,
  FunnelInputService,
  FunnelService,
  FunnelStepService,
  StorageService,
  AudienceService,
  UserService,
} from '../_services';
import { FormBuilder, Validators } from '@angular/forms';
import {
  Audience,
  CustomStepCategoryEnum,
  Funnel,
  FunnelInputTypeEnum,
  OfferTypes,
  FunnelStep,
  OfferIntents,
  OfferStages,
  Pager,
  PathNewStepSelection,
  PathSteps,
  generateStep,
  StepCategory,
  User,
  getOfferIntentDescription,
  PathStepsDescription,
  PathPopup,
  AudienceType,
} from '../_models';
import { config } from '../../config/config';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  moduleId: module.id.toString(),
  styleUrls: ['./path-edit.component.scss'],
  templateUrl: './path-edit.component.html',
})
export class PathNewComponent extends CrudSaveComponent implements OnInit, OnDestroy {
  public audiences: Audience[] = [];
  public offerIntentOptions: { value: string; label: string }[] = [];
  public funnel: Funnel;
  public pageTitle: string = 'Create new path';
  public popupTitle: string = 'Add a new step';
  public isPublish: boolean = false;
  public isPathCreated: boolean = false;
  public dynamicSteps: FunnelStep[] = [];
  public confirmationStep: FunnelStep = null;
  public rejectResponseStep: FunnelStep = null;
  public acceptResponseStep: FunnelStep = null;
  public staticSteps: FunnelStep[] = [];
  public stepInputsHavingNextStep = {};
  public isNewPath: boolean = true;
  public showNameEditor: boolean = false;
  public pathNewStepSelection = PathNewStepSelection;
  public PathSteps = PathSteps;
  public PathPopup = PathPopup;
  public popupData = {
    name: '',
    path_id: null,
    popupType: null,
    offer_intent: null,
    path_steps: [],
    path_audience: null,
    offer_type_options: [],
  };
  public offerIntentType = OfferIntents;
  public showAudiences: boolean = false;
  public PathStepsDescription = PathStepsDescription;
  public pathSteps = {
    audience: '',
    survey: '',
    downsell: '',
    upsell: '',
    // support: '', to be added
    troubleshooter: '',
  };
  public stepValidation = {
    downsell: {
      isValid: true,
      message: '',
    },
    upsell: {
      isValid: true,
      message: '',
    },
    survey: {
      isValid: true,
      message: '',
    },
  };
  public storageListener: (event: StorageEvent) => void;
  public user: User;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected funnelService: FunnelService,
    protected alertService: AlertService,
    protected formBuilder: FormBuilder,
    protected audienceService: AudienceService,
    protected modalService: NgxSmartModalService,
    protected stepService: FunnelStepService,
    protected inputService: FunnelInputService,
    protected storageService: StorageService,
    protected userService: UserService,
    protected ngxSmartModalService: NgxSmartModalService
  ) {
    super(router, location, route, funnelService, alertService);
    this.isNew = true;
    this.objectName = 'Path';
    this.stepInputsHavingNextStep[FunnelInputTypeEnum.EnterProductFunnel] = [
      FunnelInputTypeEnum.EnterProductFunnel,
      FunnelInputTypeEnum.PathNotFound,
    ];
    this.stepInputsHavingNextStep[FunnelInputTypeEnum.EnterTroubleshooter] = [
      FunnelInputTypeEnum.EnterTroubleshooter,
      FunnelInputTypeEnum.PathNotFound,
    ];
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      name: [null, [Validators.required]],
      offer_intent: [null, [Validators.required]],
      audience: [null],
      resourcetype: ['ProductFunnel', null],
      is_visual: [true],
      offer_type: [OfferTypes.SingleStep],
      child_intent: [null],
    });
    super.ngOnInit();

    this.userService.getCurrent().subscribe((user: User) => {
      this.user = user;
    });

    this.audienceService.list({ page: 1, page_size: config.maxPageSize, type: AudienceType.Retention }).subscribe(
      (result: Pager) => {
        this.audiences = result.results;
      },
      (error) => {
        this.handleError(error);
      }
    );

    const options = Object.keys(OfferStages)
      .filter((intentType) => parseInt(intentType, 10) !== OfferIntents.Legacy)
      .map((intentType) => ({
        value: intentType,
        label: OfferStages[intentType],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    this.offerIntentOptions = options;
  }

  isStaticStep(step: FunnelStep) {
    return this.isConfirmation(step) || step.type === FunnelInputTypeEnum.InputActionStatus;
  }

  setupStorageListener() {
    this.storageListener = (event: StorageEvent) => {
      const audienceId = this.storageService.get('audienceId');
      const existingAudience = this.audiences.some((audience) => audience.id === audienceId);

      if (audienceId && !existingAudience) {
        this.loading = true;
        this.audienceService.get(audienceId).subscribe({
          next: (audience: Audience) => {
            this.audiences.unshift(audience);
            this.form.controls['audience'].setValue(audience.id);
            this.updatePathAudience(+audience.id);
            this.storageService.remove('audienceId');
          },
          error: (error) => {
            this.handleError(error);
            this.loading = false;
          },
        });
      }
    };

    window.addEventListener('storage', this.storageListener);
  }

  populateExistingSteps(steps: FunnelStep[]) {
    let ignoredSteps = [];
    let toBeAddedInStaticSteps = [];
    steps.forEach((step) => {
      if (this.isStaticStep(step) || toBeAddedInStaticSteps.indexOf(step.id) > -1) {
        this.staticSteps.push(step);

        if (this.isConfirmation(step)) {
          this.confirmationStep = step;
        }
        toBeAddedInStaticSteps = toBeAddedInStaticSteps.concat(step.inputs.map((input) => input.next_step));
        return;
      }

      if (step.is_first_step || ignoredSteps.indexOf(step.id) < 0) {
        this.dynamicSteps.push(step);
      }
      if (this.isSurvey(step)) {
        ignoredSteps = ignoredSteps.concat(step.inputs.map((input) => input.next_step));
      }
    });
    this.rejectResponseStep = this.staticSteps.find(
      (step) =>
        step.type === FunnelInputTypeEnum.InputActionStatus && step.category === FunnelInputTypeEnum.ConfirmationNo
    );
    this.acceptResponseStep = this.staticSteps.find(
      (step) =>
        step.type === FunnelInputTypeEnum.InputActionStatus && step.category === FunnelInputTypeEnum.Confirmation
    );

    this.staticSteps = this.staticSteps
      .sort((a, b) => (a.category > b.category ? -1 : 1))
      .filter((step) => step.type !== FunnelInputTypeEnum.InputActionStatus);

    this.activeSteps(this.dynamicSteps);
  }

  onPublish() {
    this.isPublish = true;
    this.validateAndSubmit('publishing');
  }

  onSubmit() {
    this.isPublish = false;
    this.validateAndSubmit('saving');
  }

  private validateAndSubmit(action: string) {
    this.loading = true;
    this.submitted = true;

    this.funnelService.get(this.funnel.id).subscribe({
      next: (funnel: Funnel) => {
        let isValid = true;

        funnel.steps.forEach((step) => {
          const { hasEmptyInput } = this.validateStepInputs(step);
          if (hasEmptyInput) {
            isValid = false;
          }
        });

        this.loading = false;

        if (!isValid) {
          const message = `Please fill all required fields before ${action} the path.`;
          this.alertService.error(message);
        } else {
          super.onSubmit();
        }
      },
      error: (error) => {
        this.handleError(error);
        this.loading = false;
      },
    });
  }

  get pathTypeDescription() {
    return getOfferIntentDescription(this.funnel.offer_intent);
  }

  redirectToAudience(): void {
    const selectedAudience = this.form.value.audience;

    const url = selectedAudience
      ? this.router.serializeUrl(this.router.createUrlTree(['audiences', 'edit', selectedAudience]))
      : this.router.serializeUrl(this.router.createUrlTree(['audiences', 'new']));

    window.open(url, '_blank');
  }

  onAudienceChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    if (target.value === 'new') {
      this.form.controls['audience'].setValue(null);
      this.form.controls['audience'].updateValueAndValidity();

      this.setupStorageListener();
      this.redirectToAudience();
    } else {
      this.updatePathAudience(+target.value);
    }
  }

  updatePathAudience(audience: number): void {
    this.loading = true;
    this.funnelService.patch(this.funnel.id, { ...this.getFormData(), audience }).subscribe(
      () => (this.loading = false),
      (error) => {
        this.handleError(error);
        this.loading = false;
      }
    );
  }

  activeSteps(step: FunnelStep[]) {
    step.forEach((step) => {
      switch (step.category) {
        case FunnelInputTypeEnum.EnterProductFunnel:
          this.pathSteps.downsell = PathSteps.Downsell;
          break;
        case FunnelInputTypeEnum.EnterTroubleshooter:
          this.pathSteps.troubleshooter = PathSteps.Troubleshooter;
          break;
        case CustomStepCategoryEnum.UpsellOrder:
          this.pathSteps.upsell = PathSteps.Upsell;
          break;
      }
      if (this.isSurvey(step)) {
        this.pathSteps.survey = PathSteps.Survey;
      }
    });
  }

  validateStepInputs(step: FunnelStep): { hasEmptyInput: boolean } {
    let hasEmptyInput = false;

    if (!step.inputs || !Array.isArray(step.inputs)) {
      return { hasEmptyInput };
    }

    // Validate Survey
    const surveyStep = this.dynamicSteps.find((s) => s.type === FunnelInputTypeEnum.Radio);
    const isSurveyInvalid = surveyStep && (!surveyStep.inputs || surveyStep.inputs.length === 0);
    if (isSurveyInvalid) {
      this.stepValidation.survey = {
        isValid: false,
        message: 'Survey is required',
      };
      hasEmptyInput = true;
    }

    // Validate Audience
    if (this.showAudiences) {
      const audienceControl = this.form.get('audience');
      const isAudienceSelected = audienceControl.value;

      if (!isAudienceSelected) {
        audienceControl.setValidators(Validators.required);
        audienceControl.markAsTouched();
        audienceControl.updateValueAndValidity();

        hasEmptyInput = true;
      }
    }

    // Helper: check empty product funnels by step category
    const isEmptyProductFunnel = (condition: (input: any) => boolean) =>
      step.inputs.some(
        (input) =>
          input.type === FunnelInputTypeEnum.EnterProductFunnel &&
          Array.isArray(input.product_funnels) &&
          input.product_funnels.length === 0 &&
          condition(input)
      );

    const isDownsellStep = step.category === FunnelInputTypeEnum.EnterProductFunnel;
    const isUpsellStep = step.category === CustomStepCategoryEnum.UpsellOrder;

    if (isDownsellStep) {
      const isInvalid = isEmptyProductFunnel(() => step.previous_inputs.length !== 1);
      if (isInvalid) {
        this.stepValidation.downsell = {
          isValid: false,
          message: 'Downsell is required',
        };
        hasEmptyInput = true;
      }
    }

    if (isUpsellStep) {
      const isInvalid = isEmptyProductFunnel(() => true); // No extra condition
      if (isInvalid) {
        this.stepValidation.upsell = {
          isValid: false,
          message: 'Upsell is required',
        };
        hasEmptyInput = true;
      }
    }

    return { hasEmptyInput };
  }

  onStepValidationUpdate(event: { type: string; isValid: boolean }) {
    this.stepValidation[event.type] = {
      isValid: event.isValid,
      message: event.isValid ? '' : `${event.type} is required`,
    };
  }

  getPathType() {
    return OfferStages[this.funnel.offer_intent];
  }

  getDefaultPathStepModalData() {
    return { step_type: PathNewStepSelection.Survey };
  }

  navigateBackToPaths() {
    this.router.navigate(['/new-paths']);
  }

  openStepModal() {
    // if we haven't saved the funnel yet, save first so the steps can be created
    if (!this.funnel) {
      this.isPathCreated = true;
      this.onSubmit();
    } else {
      this.modalService.getModal('newPathStep').open();
    }
  }

  closeStepModal() {
    this.modalService.getModal('newPathStep').close();
  }

  isSurvey(step: FunnelStep) {
    const surveyCategories: StepCategory[] = [
      CustomStepCategoryEnum.Survey,
      CustomStepCategoryEnum.CancelOrderSurvey,
      CustomStepCategoryEnum.CancelTrialSurvey,
      CustomStepCategoryEnum.CancelSubSurvey,
      CustomStepCategoryEnum.ReturnSurvey,
      CustomStepCategoryEnum.PauseSurvey,
    ];

    return surveyCategories.indexOf(step.category) > -1;
  }

  isConfirmation(step: FunnelStep) {
    return [FunnelInputTypeEnum.Confirmation, FunnelInputTypeEnum.ConfirmationNo].indexOf(step.type) > -1;
  }

  newPathStepSelected(step_type: PathNewStepSelection) {
    this.closeStepModal();
    let saveStep = false;
    let step = null;

    switch (step_type) {
      case PathNewStepSelection.Survey:
        step = generateStep(FunnelInputTypeEnum.Radio, this.funnel, CustomStepCategoryEnum.Survey);
        this.pathSteps.survey = PathSteps.Survey;
        break;
      case PathNewStepSelection.DownsellOffer:
        step = generateStep(
          FunnelInputTypeEnum.EnterProductFunnel,
          this.funnel,
          FunnelInputTypeEnum.EnterProductFunnel
        );
        saveStep = true;
        this.pathSteps.downsell = PathSteps.Downsell;
        break;
      case PathNewStepSelection.UpsellOffer:
        step = generateStep(FunnelInputTypeEnum.EnterProductFunnel, this.funnel, CustomStepCategoryEnum.UpsellOrder);
        saveStep = true;
        this.pathSteps.upsell = PathSteps.Upsell;
        break;
      case PathNewStepSelection.Troubleshooter:
        step = generateStep(
          FunnelInputTypeEnum.EnterTroubleshooter,
          this.funnel,
          FunnelInputTypeEnum.EnterTroubleshooter
        );
        saveStep = true;
        this.pathSteps.troubleshooter = PathSteps.Troubleshooter;
        break;
      default:
        break;
    }

    if (step) {
      if (saveStep) {
        this.stepService.create(step).subscribe(
          (stepObject: FunnelStep) => {
            if (stepObject) {
              this.dynamicSteps.push(stepObject);
            }
            this.funnel.is_modified = true;
          },
          (error) => {
            this.handleError(error);
          }
        );
      } else {
        if (!this.dynamicSteps.some((existingStep) => existingStep.id === step.id)) {
          this.dynamicSteps.push(step);
        }
      }
    }
  }

  handleAudienceSelection() {
    this.showAudiences = true;
    this.pathSteps.audience = PathSteps.Audience;
    this.closeStepModal();
  }

  removeAudience(): void {
    const audienceControl = this.form.get('audience');
    audienceControl.reset();
    audienceControl.clearValidators();
    audienceControl.updateValueAndValidity();
    this.pathSteps.audience = '';
    this.showAudiences = false;
  }

  protected onSaveComplete(data: Funnel) {
    const openStepModal = !this.funnel;
    this.funnel = data;
    this.loading = false;
    this.stepService.setFunnelId(this.funnel.id);
    if (openStepModal && this.isPathCreated) {
      this.openStepModal();
    } else {
      super.onSaveComplete(data);
    }
  }

  getNextStepId(index: number) {
    index = index + 1;

    // last step of dynamic steps will point to the confirmation step
    if (index >= this.dynamicSteps.length && this.confirmationStep) {
      return this.confirmationStep.id;
    }

    return this.dynamicSteps[index] ? this.dynamicSteps[index].id : null;
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.dynamicSteps, event.previousIndex, event.currentIndex);
  }

  onStepSave(step: FunnelStep, index: number) {
    this.dynamicSteps[index] = step;
    this.funnel.is_modified = true;
  }

  onStepDelete(data: { step: FunnelStep; next_step: string | number }) {
    if (!('id' in data.step)) {
      // if the step is not created yet, just pop out the last entry of dynamic steps
      this.dynamicSteps.pop();
      this.onStepDeletion(data.step);
      this.loading = false;
    } else {
      // delete the step
      this.stepService.delete(data.step.id).subscribe(
        (_) => {
          this.dynamicSteps = this.dynamicSteps.filter((item) => item.id !== data.step.id);
          this.onStepDeletion(data.step);
          this.funnel.is_modified = true;
          this.loading = false;
        },
        (error) => {
          this.loading = false;
          this.handleError(error);
        }
      );
    }
  }

  onStepDeletion(step: FunnelStep) {
    switch (step.category) {
      case FunnelInputTypeEnum.EnterProductFunnel:
        this.pathSteps.downsell = '';
        break;
      case FunnelInputTypeEnum.EnterTroubleshooter:
        this.pathSteps.troubleshooter = '';
        break;
      case CustomStepCategoryEnum.UpsellOrder:
        this.pathSteps.upsell = '';
        break;
    }
    if (this.isSurvey(step)) {
      this.pathSteps.survey = '';
    }
  }

  isCancelOrderType(): boolean {
    return (
      [
        OfferIntents.CancelOrder,
        OfferIntents.CancelSubscriptionHold,
        OfferIntents.CancelTrialHold,
        OfferIntents.CancelRebillHold,
      ].indexOf(
        this.funnel.offer_intent
      ) > -1
    );
  }

  isReturnOrderType(): boolean {
    return (
      [OfferIntents.ReturnOrder, OfferIntents.ReturnSubscription, OfferIntents.ReturnTrial].indexOf(
        this.funnel.offer_intent
      ) > -1
    );
  }

  toggleNameEditor(): void {
    this.showNameEditor = !this.showNameEditor;
  }

  canPublish(): boolean {
    return this.funnel && this.funnel.is_modified;
  }

  isAllStepsSelected(): boolean {
    for (let key in this.pathSteps) {
      if (this.pathSteps.hasOwnProperty(key) && !this.pathSteps[key]) {
        return false;
      }
    }
    return true;
  }

  protected getFormData(): any {
    let data = super.getFormData();

    return data;
  }

  openEditPopup() {
    this.ngxSmartModalService.getModal('pathEditPopup').open();
  }

  closeEditPopup() {
    this.ngxSmartModalService.getModal('pathEditPopup').close();
  }

  EditPathDetails() {
    this.popupTitle = 'Edit Details';

    this.popupData = {
      ...this.popupData,
      name: this.funnel.name,
      path_id: this.funnel.id,
      popupType: PathPopup.EditDetails,
      offer_intent: this.funnel.offer_intent,
      path_steps: [],
      offer_type_options: this.offerIntentOptions,
    };
    this.openEditPopup();
  }

  savePath(data: { name: string }): void {
    this.ngxSmartModalService.getModal('pathEditPopup').close();
    this.funnel.name = data.name;
  }

  popupIconMap: Partial<Record<PathPopup, string>> = {
    [PathPopup.Create]: '/assets/stepAssets/create-icon.svg',
    [PathPopup.Delete]: '/assets/stepAssets/delete-icon.svg',
    [PathPopup.Duplicate]: '/assets/stepAssets/duplicate.svg',
    [PathPopup.EditDetails]: '/assets/stepAssets/rename.svg',
  };
}
